import { useMemo, useCallback } from "react";
import { useFormContext, useWatch } from "react-hook-form";
import moment from "moment-timezone";
import { useScheduleContext } from "../../../../../hooks/context/useScheduleContext";
import { getOrderedWeekdays } from "../../../../../helpers/getOrderedWeekdays";
import { getSectionsBounds } from "../../../../../helpers/form/sections";
import { combineDateAndTime, renderDurationAsFormat } from "../../../../../../../../utils/helpers/date";
import { getHourTypeColor } from "../../../../../../../../utils/helpers/hourType";

const sectionColors: Record<string, string> = {
    regular: "#314a9f",
    transfer: "#5428e0",
    break: "#fe9700",
    overtime: "#f8219c",
    uat: `repeating-linear-gradient(135deg,
        #ddd,
        #ddd 10px,
        #eee 10px,
        #eee 20px
    )`,
};

export const getSectionsForDisplay = (baseSections: any[], date: any) => {
    const newSections: any[] = [];
    const filtered = baseSections?.filter(s => s?.start && s?.end) || [];
    if (!filtered.length) return newSections;

    let start = combineDateAndTime(moment(date), moment(filtered[0]?.start));
    let end = combineDateAndTime(moment(date), moment(filtered[0]?.end));

    if (moment(end).isSameOrBefore(moment(start))) {
        end = moment(end).add(1, "day");
    }

    newSections.push({ ...filtered[0], start, end });

    for (let i = 1; i < filtered.length; i++) {
        const prev = filtered[i - 1];
        const curr = filtered[i];

        let gapStart = combineDateAndTime(moment(date), moment(prev?.end));
        let gapEnd = combineDateAndTime(moment(date), moment(curr?.start));

        if (moment(gapEnd).isBefore(moment(gapStart), "seconds")) {
            gapEnd = moment(gapEnd).add(1, "day");
        }

        const duration = moment(gapEnd).diff(moment(gapStart), "seconds");
        if (duration > 0) {
            newSections.push({
                ...prev,
                type: "uat",
                start: gapStart,
                end: gapEnd,
                duration: renderDurationAsFormat(duration, "HH:mm"),
            });
        }

        let s = combineDateAndTime(moment(date), moment(curr?.start));
        let e = combineDateAndTime(moment(date), moment(curr?.end));
        if (moment(e).isBefore(moment(s))) {
            e = moment(e).add(1, "day");
        }

        newSections.push({
            ...curr,
            start: s,
            end: e,
            duration: renderDurationAsFormat(moment(e).diff(moment(s), "seconds"), "HH:mm"),
        });
    }

    return newSections;
};

function Progress() {
    const { control } = useFormContext();
    const [sections, _date] = useWatch({ control, name: ["sections", "date"] });
    const { selectedCellOrEvent } = useScheduleContext();
    const orderedWeekdays = getOrderedWeekdays();

    const date = useMemo(() => {
        const isStrictDate = moment(_date, "YYYY-MM-DD", true).isValid();
        return selectedCellOrEvent?.isTemplate
            ? (isStrictDate ? _date : moment().weekday(orderedWeekdays.indexOf(_date)).format("YYYY-MM-DD"))
            : _date;
    }, [selectedCellOrEvent?.isTemplate, _date, orderedWeekdays]);

    const sectionsForDisplay = useMemo(() => {
        return getSectionsForDisplay(sections, date)
    }, [sections, date]);

    const { startDate, endDate, durationLabel } = useMemo(() => {
        if (!sectionsForDisplay?.length) return {} as { startDate?: Date, endDate?: Date, durationLabel?: string };

        // sectionsForDisplay is already anchored to `date` and rolled over midnight, so
        // take the shift's bounds from it: the widest span the sections cover, rather than
        // the first and last rows, which miss a section ending after the ones below it.
        const { start: startDate, end: endDate } = getSectionsBounds(sectionsForDisplay);

        if (!startDate || !endDate) return {};

        const totalSeconds = moment(endDate).diff(moment(startDate), "seconds");
        const hours = Math.floor(totalSeconds / 3600);
        const minutes = Math.floor((totalSeconds % 3600) / 60);

        const durationLabel =
            hours > 0 && minutes === 0
                ? `${hours} h`
                : hours === 0
                    ? `${minutes} m`
                    : `${hours} h ${minutes} m`;

        return { startDate, endDate, durationLabel };
    }, [sectionsForDisplay]);

    const intervals = useMemo(() => {
        return sectionsForDisplay?.map(section => {
            const duration = moment(section.end).diff(moment(section.start), "seconds");
            return { type: section.type, hourType: section?.payCode?.hourType, duration };
        });
    }, [sectionsForDisplay]);

    const totalDuration = useMemo(
        () => intervals?.reduce((t, i) => t + i.duration, 0) || 0,
        [intervals]
    );

    const getWidth = useCallback(
        (duration: number) => (totalDuration ? (duration / totalDuration) * 100 : 0),
        [totalDuration]
    );

    return (
        <div className="tw-mb-4">
            <div className="tw-flex tw-items-center tw-justify-between tw-mb-1 tw-text-sm">
                <span>
                    {startDate ? moment(startDate).format("hh:mm A") : "-- : --"}
                </span>

                <span className="tw-font-semibold">
                    {durationLabel || "0 h"}
                </span>

                <span>
                    {endDate ? moment(endDate).format("hh:mm A") : "-- : --"}
                </span>
            </div>

            <div className="tw-flex tw-overflow-hidden tw-rounded tw-bg-neutral-200 tw-h-3">
                {intervals?.map((i, idx) => (
                    <div
                        key={`${i.type}-${idx}`}
                        style={{
                            width: `${getWidth(i.duration)}%`,
                            background: sectionColors[i.type] || getHourTypeColor(i.hourType),
                        }}
                    />
                ))}
            </div>
        </div>
    );
}

export default Progress;
